/* eslint-disable camelcase */
import axios, {AxiosError} from 'axios';

import type {ProductProps} from '@/molecules/Cards/Product/typings';
import type {sizeUnitType} from '@/molecules/Cards/Product/typings';
import type {DimensionsProps} from '@/utils/helpers/string';
import type {ProductFullDetails} from './typings';

import constantsFactory from '@/utils/constants';

const {API} = constantsFactory();
const API_BASE_URL = process.env.API_BASE as string;

/**
 * @description Parses dimensions from a string.
 * - Handles both cases with and without depth.
 * - Supports dimensions in cm and in.
 * - Returns an object with width, height, depth, units, and type.
 * - Returns null if the string does not match the expected format.
 * @param {string} dimensionString - The dimensions string served by API.
 * Example: "110 x 70 x 4 cm (unframed) / 110 x 70 cm (actual image size)"
 * Example: "28.35 x 28.35 x 1.18 in (framed) / 27.56 x 27.56 x 0.79 in (unframed)"
 * Example: '39 x 38 x 0.1 cm   /  39 x 38 cm (unframed)'
 * Example: "30 x 40 cm (framed)"
 * @return {DimensionsProps | null} dimensions object or `null` if the string does not match the expected format.
 */
export function parseDimensionsFromString(dimensionString: string): DimensionsProps | null {
  /** Take the first part before slash */
  const mainPart = dimensionString.split('/')[0].trim();

  /** Remove any parenthetical info */
  const cleaned = mainPart.replace(/\(.*?\)/g, '').trim();

  /** Regexp: width x height x (optional) depth units */
  const regex = /([\d.]+)\s*x\s*([\d.]+)(?:\s*x\s*([\d.]+))?\s*(cm|in)/i;
  const match = cleaned.match(regex);

  if (!match) return null;

  const width = parseFloat(match[1]);
  const height = parseFloat(match[2]);
  const depth = match[3] ? parseFloat(match[3]) : null;
  const units = match[4].toLowerCase() as 'cm' | 'in';

  /** Optionally extract type from parenthesis in original string */
  const typeMatch = mainPart.match(/\((.*?)\)/);
  const type = typeMatch ? typeMatch[1].trim() : 'unframed';

  return {width, height, depth, units, type};
}

/**
 * @description Formats the price object details.
 * - Converts the full price object to a more usable format.
 * - Returns an object with `prices` and `original_prices` if sales are applicable.
 * @param {object} fullPrice
 * @return {object} An object containing formatted prices and original prices if sales are applicable.
 */
export const formatFullPrice = (
  fullPrice: ProductFullDetails['pricing'],
): {prices: ProductProps['prices']; original_prices: ProductProps['original_prices']} => {
  const prices: ProductProps['prices'] = {
    GBP: 0,
    USD: 0,
    EUR: 0,
    CAD: 0,
    AUD: 0,
  };
  let originalPrices: ProductProps['original_prices'] = null;

  Object.entries(fullPrice).forEach(([currency, price]) => {
    if (price.current_amount) {
      prices[currency] = price.current_amount;
    }
    if (price.original_amount > price.current_amount) {
      if (!originalPrices) {
        originalPrices = {
          GBP: 0,
          USD: 0,
          EUR: 0,
          CAD: 0,
          AUD: 0,
        };
      }
      originalPrices[currency] = price.original_amount;
    }
  });

  return {prices, original_prices: originalPrices};
};

/**
 * @description Formats the full product details to match the ProductProps structure.
 * - Parses dimensions using `parseDimensionsFromString` util function.
 * - Formats prices using the `formatFullPrice` util function.
 * - Returns the `ProductProps` object.
 * @param {ProductFullDetails} fullProduct
 * @param {sizeUnitType} units - The size units to use for dimensions (default is 'cm').
 * @return {ProductProps} A formatted ProductProps object.
 */
export const formatFullProductToProductProps = (
  fullProduct: ProductFullDetails,
  units: sizeUnitType = 'cm',
): ProductProps => {
  const dimensionsText =
    units === 'cm' ? fullProduct.dimensions_text_cm : fullProduct.dimensions_text_in;
  const dimensions = parseDimensionsFromString(dimensionsText) as DimensionsProps;
  const {prices, original_prices} = formatFullPrice(fullProduct.pricing);

  return {
    id: fullProduct.id,
    name: fullProduct.name,
    slug: fullProduct.slug,
    quantity: fullProduct.quantity,
    prices,
    original_prices,
    artist_id: fullProduct.artist_id,
    artist_name: fullProduct.artist_name,
    artist_slug: fullProduct.artist_slug,
    category_name: fullProduct.category_full_name,
    average_colour: fullProduct.average_colour,
    category_slug: fullProduct.category_slug,
    dimensions,
    is_in_stock: fullProduct.is_in_stock,
    images:
      fullProduct.thumbor_images.length > 0
        ? fullProduct.thumbor_images.map(({path, width, height}, index) => ({
            index,
            path,
            width,
            height,
            type: null, // Assuming `type` is not used in this context
          }))
        : [
            {
              index: 0,
              path: fullProduct.full_frontal_image.url,
              width: fullProduct.full_frontal_image.width,
              height: fullProduct.full_frontal_image.height,
              type: null, // Assuming `type` is not used in this context
            },
          ],
    style_slug: fullProduct.style_slug,
    subject_slug: fullProduct.subject_slug,
    le_prints: null, // Assuming `le_prints` is not used in this context
    cb: 'b', // Assuming `'b'` can be a constant value for this context
  };
};

/**
 * @description Creates AbortControllers and AbortSignals for a list of product slugs.
 * @param {string[]} slugs - An array of product slugs to create AbortControllers for.
 * @return {[Record<string, AbortController>, Record<string, AbortSignal>]} A tuple containing the AbortControllers and AbortSignals.
 */
export const getAbortControllers = (
  slugs: string[],
): [Record<string, AbortController>, Record<string, AbortSignal>] => {
  const abortControllers = slugs.reduce(
    (abortControllers, slug) => {
      abortControllers[slug] = new AbortController();
      return abortControllers;
    },
    {} as Record<string, AbortController>,
  );

  const abortSignals = Object.entries(abortControllers).reduce(
    (signals, [slug, controller]) => {
      signals[slug] = controller.signal;
      return signals;
    },
    {} as Record<string, AbortSignal>,
  );

  return [abortControllers, abortSignals];
};

/**
 *@description Fetches a product by its slug.
 * - Uses the API_BASE_URL and API constants to construct the endpoint.
 * - Returns the full product details.
 * @param {string} productSlug
 * @param {AbortSignal} signal - Optional signal to cancel the request.
 * @return {Promise<ProductFullDetails>} A promise that resolves to the full product details.
 */
export const fetchProductBySlug = async (productSlug: string, signal?: AbortSignal) => {
  const {data} = await axios.get<ProductFullDetails>(
    `${API_BASE_URL}${API.PRODUCT}${productSlug}/full/`,
    {
      headers: {Accept: 'application/json'},
      withCredentials: true,
      signal,
    },
  );

  return data;
};

/**
 * @description Fetches products based on Exponea recommendations.
 * High latency and heavy-lift workaround for the BloomReach/Exponea SDK
 * that does not return product data required by our application.
 * - Uses the slugs from recommendations to fetch full product details.
 * - Formats the full product details to match the ProductProps structure.
 * - Handles different display size units (cm or in).
 * - Returns an array of `ProductProps`.
 * @param {object} params
 * @param {string[]} params.slugs
 * @param {Record<string, AbortSignal>| undefined} params.abortSignals - (optional) signals to cancel requests for each slug.
 * @return {Promise<ProductFullDetails[]>} An array of Product Full Details.
 */
export const fetchProductsBySlugs = async ({
  slugs,
  abortSignals,
}: {
  slugs: string[];
  abortSignals?: Record<string, AbortSignal>;
}): Promise<ProductFullDetails[]> => {
  const requests = slugs.map((slug) => fetchProductBySlug(slug, abortSignals?.[slug]));
  const results = await Promise.allSettled(requests);

  return results.reduce((fulfilledOnly, result) => {
    if (result.status === 'fulfilled') {
      fulfilledOnly.push(result.value);
    }
    return fulfilledOnly;
  }, [] as ProductFullDetails[]);
};

export const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.message;
  } else if (error instanceof Error) {
    return error.message;
  }
  return 'Fetch products error';
};

/**
 * @description Filters the slugs to only include those that haven't been fetched yet.
 * @param {object} params - The parameters containing slugs and already fetched slugs.
 * @param {string[]} params.slugs - The array of all slugs to filter.
 * @param {Set<string>} params.alreadyFetched - The set of already fetched slugs.
 * @returns An array of slugs haven't been fetched yet.
 */
export const getSlugsToFetch = ({
  slugs,
  alreadyFetched,
  take,
}: {
  slugs: string[];
  alreadyFetched: Set<string>;
  take: number;
}) => {
  return slugs.filter((slug) => !alreadyFetched.has(slug)).slice(0, take);
};
